"use client";
import { useEffect, useRef, useState } from "react";
import type { SearchResult } from "@/types/search";

export function useGlobalSearch(query: string, enabled: boolean) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [retry, setRetry] = useState(0);
  const retryAt = useRef(0);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(
      () => setCooldown((value) => Math.max(0, value - 1)),
      1000,
    );
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    const term = query.normalize("NFKC").trim();
    setResults([]);
    setLoading(false);
    if (!enabled || term.length < 2) {
      setError(null);
      return;
    }
    if (Date.now() < retryAt.current) {
      setError("Please wait before searching again.");
      return;
    }
    setError(null);
    const timer = setTimeout(async () => {
      setLoading(true);
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 6500);
      try {
        const response = await fetch(
          `/api/search?${new URLSearchParams({ q: term, limit: "5" })}`,
          { signal: controller.signal },
        );
        const body = await response.json();
        if (cancelled || controller.signal.aborted) return;
        if (!response.ok) {
          if ([429, 503].includes(response.status)) {
            const seconds = Math.min(
              3600,
              Math.max(1, Number(response.headers.get("retry-after")) || 2),
            );
            retryAt.current = Date.now() + seconds * 1000;
            setCooldown(seconds);
          }
          setError(
            typeof body.message === "string"
              ? body.message
              : "Search is unavailable. Please try again.",
          );
          return;
        }
        setResults(Array.isArray(body.results) ? body.results.slice(0, 5) : []);
      } catch {
        if (!cancelled)
          setError(
            timedOut
              ? "Search took too long. Please try again."
              : "Search could not connect. Please try again.",
          );
      } finally {
        clearTimeout(timeout);
        if (!cancelled) setLoading(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, enabled, retry]);

  return {
    results,
    loading,
    error,
    cooldown,
    retry: () => {
      if (Date.now() >= retryAt.current) setRetry((value) => value + 1);
    },
  };
}
